import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { PlatformConnection } from '../types';
import { visiblePlatforms, isPlatformAvailable, getPlatformName } from '../config/platforms';
import { ImportConnections } from '../components/ImportConnections';

const API_URL = process.env.REACT_APP_API_URL || '';

export const PlatformConnections: React.FC = () => {
  const { token, user } = useAuth();
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rescanning, setRescanning] = useState(false);
  // Demo accounts (platform reviewers) get a read-only view of connections
  const readOnly = user?.role === 'demo';

  const platforms = visiblePlatforms();

  // Load connections on mount
  useEffect(() => {
    loadConnections();

    // Check for OAuth callback messages in URL
    const params = new URLSearchParams(window.location.search);
    const errorMsg = params.get('error');
    const successMsg = params.get('success');

    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      // Clean up URL
      window.history.replaceState({}, '', '/connections');
    }

    if (successMsg) {
      setSuccess(decodeURIComponent(successMsg));
      loadConnections(); // Reload connections after successful OAuth
      // Clean up URL
      window.history.replaceState({}, '', '/connections');
    }
    // Once, on arrival from the OAuth redirect: re-running when loadConnections' identity
    // changes would re-read a query string that has already been cleared
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConnections = async () => {
    try {
      const response = await fetch(`${API_URL}/api/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      } else {
        setError('Failed to load connections');
      }
    } catch (err) {
      setError('Network error loading connections');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platformId: string) => {
    if (isPlatformAvailable(platformId)) {
      try {
        setError(null);

        // Get OAuth authorization URL from backend
        const response = await fetch(`${API_URL}/api/oauth/${platformId}/authorize`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Redirect to OAuth page
          window.location.href = data.authUrl;
        } else {
          setError(`Failed to initiate ${platformId} connection`);
        }
      } catch (err) {
        setError(`Network error connecting to ${platformId}`);
      }
    } else {
      setError(`${platformId} integration coming soon!`);
    }
  };

  const handleDisconnect = async (connectionId: string, platformName: string) => {
    if (!window.confirm(`Are you sure you want to disconnect ${platformName}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/connections/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess(`${platformName} disconnected successfully`);
        loadConnections();
      } else {
        setError(`Failed to disconnect ${platformName}`);
      }
    } catch (err) {
      setError('Network error disconnecting platform');
    }
  };

  const isPlatformConnected = (platformId: string) => {
    return connections.find(conn => conn.platform === platformId && conn.isActive);
  };

  const handleLinkedInOrgChange = async (connectionId: string, organizationId: string | null) => {
    try {
      const response = await fetch(`${API_URL}/api/connections/${connectionId}/linkedin-org`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organizationId: organizationId === 'profile' ? null : organizationId }),
      });

      if (response.ok) {
        setSuccess('Default posting destination updated');
        loadConnections();
      } else {
        setError('Failed to update posting destination');
      }
    } catch (err) {
      setError('Network error updating posting destination');
    }
  };

  const handleFacebookPageChange = async (connectionId: string, pageId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/connections/${connectionId}/facebook-page`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pageId }),
      });

      if (response.ok) {
        setSuccess('Default Facebook page updated');
        loadConnections();
      } else {
        setError('Failed to update Facebook page');
      }
    } catch (err) {
      setError('Network error updating Facebook page');
    }
  };

  const handleInstagramAccountChange = async (connectionId: string, accountId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/connections/${connectionId}/instagram-account`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId }),
      });

      if (response.ok) {
        setSuccess('Default Instagram account updated');
        loadConnections();
      } else {
        setError('Failed to update Instagram account');
      }
    } catch (err) {
      setError('Network error updating Instagram account');
    }
  };

  const handleRescanInstagram = async (connectionId: string) => {
    setRescanning(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_URL}/api/connections/${connectionId}/refresh-instagram`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();

      if (response.ok) {
        if (data.count > 0) {
          setSuccess(data.message);
        } else {
          // Not an error exactly, but the user needs to know why nothing appeared
          setError(data.message);
        }
        loadConnections();
      } else {
        setError(data.error || 'Failed to rescan for Instagram accounts');
      }
    } catch (err) {
      setError('Network error rescanning for Instagram accounts');
    } finally {
      setRescanning(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Platform Connections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your social media accounts to start posting
        </Typography>
      </Box>

      {/* Offered before the Connect buttons: if the same login is already connected in another
          workspace, reusing it is the shorter path than re-authorising it. */}
      <ImportConnections onImported={loadConnections} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        {platforms.map((platform) => {
          // Instagram has no connection row of its own: it is reached through the
          // Facebook connection, so its card reads that connection's state.
          const hostPlatform = platform.connectsVia || platform.id;
          const connection = isPlatformConnected(hostPlatform);
          const available = platform.status === 'available';
          const isVia = Boolean(platform.connectsVia);
          const igAccounts = connection?.instagramAccounts || [];
          const igReady = !isVia || igAccounts.length > 0;

          return (
            <Box key={platform.id}>
              <Card sx={{ height: '100%', opacity: available ? 1 : 0.6 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{platform.name}</Typography>
                    <Chip
                      label={
                        !available ? 'Coming soon'
                          : !connection ? 'Not Connected'
                          : !igReady ? 'No account found'
                          : connection.health === 'expired' ? 'Needs reconnecting'
                          : connection.health === 'refreshing' ? 'Renewing'
                          : 'Connected'
                      }
                      color={
                        !available || !connection || !igReady ? 'default'
                          : connection.health === 'expired' ? 'error'
                          : connection.health === 'refreshing' ? 'warning'
                          : 'success'
                      }
                      size="small"
                      variant={available ? 'filled' : 'outlined'}
                    />
                  </Box>

                  {platform.note && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {platform.note}
                    </Typography>
                  )}

                  {isVia && !connection && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Connect {getPlatformName(hostPlatform)} first — Instagram is reached
                      through the Facebook Page it is linked to.
                    </Typography>
                  )}

                  {isVia && connection && igAccounts.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No Instagram Business account was found on your Facebook Pages. Either
                      link one in the Page's settings and use Rescan below, or — if this
                      connection predates Instagram support — reconnect{' '}
                      {getPlatformName(hostPlatform)} so it can ask for Instagram access.
                    </Typography>
                  )}

                  {isVia && connection && igAccounts.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel size="small">Post to</InputLabel>
                      <Select
                        size="small"
                        value={igAccounts.find(a => a.isDefault)?.id || ''}
                        onChange={(e) => handleInstagramAccountChange(connection.id, e.target.value)}
                        disabled={readOnly}
                        label="Post to"
                      >
                        {igAccounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.username ? `@${account.username}` : account.igUserId}
                            {account.pageName ? ` — via ${account.pageName}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {connection && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: connection.healthDetail ? 1 : 2 }}>
                      {connection.accountName}
                    </Typography>
                  )}

                  {connection?.healthDetail && (
                    <Alert
                      severity={connection.health === 'expired' ? 'warning' : 'info'}
                      sx={{ mb: 2, py: 0, fontSize: '0.8rem' }}
                    >
                      {connection.healthDetail}
                    </Alert>
                  )}

                  {/* Expiring soon, or renewal failing: things the health line cannot say,
                      because the token still works today. An expired token is already covered
                      above. */}
                  {connection?.attention && connection.attention.state !== 'expired' && (
                    <Alert
                      severity={connection.attention.state === 'renewal_failed' ? 'error' : 'warning'}
                      sx={{ mb: 2, py: 0, fontSize: '0.8rem' }}
                    >
                      {connection.attention.message}
                    </Alert>
                  )}

                  {connection && platform.id === 'linkedin' && connection.organizations && connection.organizations.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel size="small">Post to</InputLabel>
                      <Select
                        size="small"
                        value={connection.organizations.find(org => org.isDefault)?.id || 'profile'}
                        onChange={(e) => handleLinkedInOrgChange(connection.id, e.target.value)}
                        disabled={readOnly}
                        label="Post to"
                      >
                        <MenuItem value="profile">Personal Profile ({connection.accountName})</MenuItem>
                        {connection.organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.organizationName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {connection && platform.id === 'facebook' && connection.pages && connection.pages.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel size="small">Post to Page</InputLabel>
                      <Select
                        size="small"
                        value={connection.pages.find(page => page.isDefault)?.id || ''}
                        onChange={(e) => handleFacebookPageChange(connection.id, e.target.value)}
                        disabled={readOnly}
                        label="Post to Page"
                      >
                        {connection.pages.map((page) => (
                          <MenuItem key={page.id} value={page.id}>
                            {page.pageName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {readOnly ? (
                    <Button variant="outlined" fullWidth size="small" disabled>
                      {connection ? 'Connected' : 'Not connected'}
                    </Button>
                  ) : isVia ? (
                    // Instagram is not connected or disconnected on its own — it is
                    // rediscovered from the Facebook pages it hangs off.
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={!connection || rescanning}
                      onClick={() => connection && handleRescanInstagram(connection.id)}
                    >
                      {rescanning ? 'Rescanning…' : 'Rescan for Instagram accounts'}
                    </Button>
                  ) : (
                    <Button
                      variant={connection ? 'outlined' : 'contained'}
                      fullWidth
                      size="small"
                      disabled={!available}
                      onClick={() => connection
                        ? handleDisconnect(connection.id, platform.name)
                        : handleConnect(platform.id)
                      }
                    >
                      {!available ? 'Unavailable' : connection ? 'Disconnect' : 'Connect'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
